/* eslint-disable class-methods-use-this */

import Dispatcher from "flux/lib/Dispatcher";
import ReduceStore from "flux/lib/FluxReduceStore";

import { Dot } from "../../../foundation/api/model";
import { TransformedAIReviewDot } from "../../../foundation/api/model/AIReview/AIReviewDot";
import CreateDotParameters from "../../../foundation/api/model/dots/CreateDotParameters";
import DraftDot from "../../../foundation/model/DraftDot";
import RemarkType from "../../../foundation/model/RemarkType";
import BasicRecord from "../../../foundation/utils/BasicRecord";
import { transformAIReviewDots } from "../../../foundation/utils/transform";
import * as ActivitiesActionPayloads from "../activities/ActivitiesActionPayloads";
import ActivitiesActionTypes from "../activities/ActivitiesActionTypes";
import * as ApprovalsActionPayloads from "../approvals/ApprovalsActionPayloads";
import ApprovalsActionTypes from "../approvals/ApprovalsActionTypes";
import * as BarrelActionPayloads from "../barrel/BarrelActionPayloads";
import BarrelActionTypes from "../barrel/BarrelActionTypes";
import * as DotCommentsActionPayloads from "../dotComments/DotCommentsActionPayloads";
import DotCommentsActionTypes from "../dotComments/DotCommentsActionTypes";
import DotsAndAnnotationsReducer from "../dotsAndAnnotationsReducer/DotsAndAnnotationsReducer";
import * as NotificationsActionPayloads from "../notifications/NotificationsActionPayloads";
import NotificationsActionTypes from "../notifications/NotificationsActionTypes";
import * as ProjectActionPayloads from "../project/ProjectActionPayloads";
import ProjectActionTypes from "../project/ProjectActionTypes";

import { AllPayloads } from "../payloads";

import * as ScreenActionPayloads from "./ScreenActionPayloads";
import ScreenActionTypes from "./ScreenActionTypes";

type State = {
    flash: boolean;
    dots: {
        [pid: string]: {
            [sid: string]: Dot[];
        };
    };
    aiReviewDots: {
        [pid: string]: {
            [arid: string]: {
                [sid: string]: TransformedAIReviewDot[];
            };
        };
    };
};

type DotType = Dot | DraftDot | (CreateDotParameters & { _id: string; });

class DotsDataStore extends ReduceStore<State, AllPayloads> {
    reducer: DotsAndAnnotationsReducer<State, DotType, "dots">;

    constructor(dispatcher: Dispatcher<AllPayloads>) {
        super(dispatcher);
        this.reducer = new DotsAndAnnotationsReducer({ propertyName: "dots" });
    }

    getInitialState(): State {
        return {
            flash: false,
            dots: {},
            aiReviewDots: {}
        };
    }

    dotsFlashed(state: State): State {
        return {
            ...state,
            flash: false
        };
    }

    setProjectDots(state: State, {
        pid,
        dotsMap
    }: ProjectActionPayloads.SetProjectDots): State {
        return this.reducer.setProjectDotsAndAnnotations(
            state,
            { pid, data: dotsMap }
        );
    }

    setDots(state: State, {
        pid,
        sid,
        dots
    }: ScreenActionPayloads.SetDots): State {
        return this.reducer.setDotsAndAnnotations(
            state,
            { pid, sid, data: dots }
        );
    }

    updateDotReadStatusFromSelectingDot(state: State, {
        pid,
        sid,
        did,
        markAsRead = false
    }: ScreenActionPayloads.SelectDot): State {
        if (!markAsRead) {
            return state;
        }

        return this.reducer.updateCommentReadStatuses(state, {
            pid,
            read: true,
            dots: [{
                did,
                sid
            }]
        });
    }

    setDotsForIntermediateLoading(state: State, {
        dotsDetails: {
            pid,
            sid,
            dots
        }
    }: BarrelActionPayloads.LoadIntermediate): State {
        return this.reducer.setDotsAndAnnotations(
            state,
            { pid, sid, data: dots }
        );
    }

    createNote(
        state: State,
        { pid, sid, dot }: { pid: string; sid: string; dot: DotType; }
    ) {
        return this.reducer.insertDotAndAnnotation(
            state,
            { pid, sid, data: dot }
        );
    }

    updateDot(state: State, {
        pid,
        sid,
        did: oldDid,
        dotData
    }: ScreenActionPayloads.UpdateDot): State {
        return this.reducer.updateDotAndAnnotation(
            state,
            { pid, sid, daid: oldDid, data: dotData }
        );
    }

    updateDotPosition(state: State, {
        pid,
        sid,
        did,
        x,
        y
    }: ScreenActionPayloads.UpdateDotPosition): State {
        return this.reducer.updateDotAndAnnotation(
            state,
            { pid, sid, daid: did, data: { x, y } }
        );
    }

    updateAIDotPosition(state: State, {
        pid,
        approvalsId: arid,
        sid,
        did,
        x,
        y
    }: ScreenActionPayloads.UpdateAIDotPosition): State {
        if (!pid || !sid || !did || !arid) {
            return state;
        }

        const updatedDots = state.aiReviewDots?.[pid][arid][sid]
            ?.map(dot => (
                dot._id === did
                    ? { ...dot, x, y }
                    : dot
            ));

        if (!updatedDots?.length) {
            return state;
        }

        return {
            ...state,
            aiReviewDots: {
                ...state.aiReviewDots,
                [pid]: {
                    ...state.aiReviewDots[pid],
                    [arid]: {
                        ...state.aiReviewDots[pid][arid],
                        [sid]: updatedDots
                    }
                }
            }
        };
    }

    updateDotStartPosition(state: State, {
        pid,
        sid,
        did,
        xStart,
        yStart
    }: ScreenActionPayloads.UpdateDotStartPosition): State {
        return this.reducer.updateDotAndAnnotation(
            state,
            { pid, sid, daid: did, data: { xStart, yStart } }
        );
    }

    updateDotStatus(state: State, {
        pid,
        sid,
        did: dotId,
        status
    }: ScreenActionPayloads.UpdateDotStatus): State {
        return this.reducer.updateDotAndAnnotation(
            state,
            { pid, sid, daid: dotId, data: { status } }
        );
    }

    updateAIDot(state: State, {
        pid,
        sid,
        airid,
        did,
        dotData
    }: ApprovalsActionPayloads.UpdateAIReviewScreenDot): State {
        if (!pid || !sid || !did || !airid) {
            return state;
        }

        const dots = state.aiReviewDots[pid]?.[airid]?.[sid];
        if (!dots || dots.length === 0) {
            return state;
        }

        return {
            ...state,
            aiReviewDots: {
                ...state.aiReviewDots,
                [pid]: {
                    ...state.aiReviewDots[pid],
                    [airid]: {
                        ...state.aiReviewDots[pid][airid],
                        [sid]: dots.map(dot => (
                            dot._id === did
                                ? { ...dot, ...dotData }
                                : dot
                        ))
                    }
                }
            }
        };
    }

    updateAIDotStatus(state: State, {
        pid,
        sid,
        airid,
        did,
        status
    }: ApprovalsActionPayloads.UpdateAIReviewScreenDotStatus): State {
        if (!pid || !sid || !did || !airid) {
            return state;
        }

        const dots = state.aiReviewDots[pid]?.[airid]?.[sid];
        if (!dots || dots.length === 0) {
            return state;
        }

        return {
            ...state,
            aiReviewDots: {
                ...state.aiReviewDots,
                [pid]: {
                    ...state.aiReviewDots[pid],
                    [airid]: {
                        ...state.aiReviewDots[pid][airid],
                        [sid]: dots.map(dot => (
                            dot._id === did
                                ? { ...dot, status }
                                : dot
                        ))
                    }
                }
            }
        };
    }

    createAiReviewScreenDotRating(state: State, {
        pid,
        sid,
        airid,
        did,
        rating
    }: ApprovalsActionPayloads.CreateAIReviewScreenDotRating): State {
        if (!pid || !sid || !did || !airid) {
            return state;
        }

        const dots = state.aiReviewDots[pid]?.[airid]?.[sid];
        if (!dots || dots.length === 0) {
            return state;
        }

        return {
            ...state,
            aiReviewDots: {
                ...state.aiReviewDots,
                [pid]: {
                    ...state.aiReviewDots[pid],
                    [airid]: {
                        ...state.aiReviewDots[pid][airid],
                        [sid]: dots.map(dot => (
                            dot._id === did
                                ? { ...dot, rating }
                                : dot
                        ))
                    }
                }
            }
        };
    }

    updateDotColor(
        state: State,
        { pid, sid, did, color }: ScreenActionPayloads.UpdateDotColor
    ): State {
        return this.reducer.updateDotAndAnnotation(
            state,
            { pid, sid, daid: did, data: { color } }
        );
    }

    addDot(
        state: State,
        { pid, sid, dot }: { pid: string; sid: string; dot: DotType; }
    ) {
        return this.reducer.insertDotAndAnnotation(
            state,
            { pid, sid, data: dot }
        );
    }

    createComment(state: State, {
        pid,
        sid,
        did,
        comment
    }: ScreenActionPayloads.CreateComment): State {
        return this.reducer.insertDotComment(
            state,
            { pid, sid, daid: did, comment }
        );
    }

    addComment(state: State, {
        pid,
        sid,
        did,
        comment
    }: ScreenActionPayloads.AddComment): State {
        return this.reducer.insertDotComment(
            state,
            { pid, sid, daid: did, comment }
        );
    }

    updateComment(state: State, {
        pid,
        sid,
        did,
        cmid,
        commentData
    }: ScreenActionPayloads.UpdateComment): State {
        return this.reducer.updateDotComment(state, {
            pid, sid, daid: did, cmid, commentData
        });
    }

    createReaction(state: State, {
        pid,
        sid,
        did,
        cmid,
        reaction
    }: ScreenActionPayloads.CreateReaction): State {
        return this.reducer.insertReaction(
            state,
            {
                pid, sid, daid: did, cmid, reaction
            }
        );
    }

    addReaction(state: State, {
        pid,
        sid,
        did,
        cmid,
        reaction
    }: ScreenActionPayloads.AddReaction): State {
        return this.reducer.insertReaction(
            state,
            {
                pid, sid, daid: did, cmid, reaction
            }
        );
    }

    updateReaction(state: State, {
        pid,
        sid,
        did,
        cmid,
        cmrid,
        reactionData
    }: ScreenActionPayloads.UpdateReaction): State {
        return this.reducer.updateReaction(
            state,
            {
                pid, sid, daid: did, cmid, cmrid, reactionData
            }
        );
    }

    removeReaction(state: State, {
        pid,
        sid,
        did,
        cmid,
        cmrid,
        uid
    }: ScreenActionPayloads.RemoveReaction): State {
        return this.reducer.removeReaction(
            state,
            {
                pid, sid, daid: did, cmid, cmrid, uid
            }
        );
    }

    updateCommentReadStatuses(state: State, {
        parameters
    }: DotCommentsActionPayloads.UpdateDotCommentReadStatus) {
        return this.reducer.updateCommentReadStatuses(state, {
            ...parameters
        });
    }

    updateDotReadStatusFromNotifications(state: State, {
        notification,
        eventIds,
        markedAsRead
    }: NotificationsActionPayloads.SetEventReadStatus) {
        return this.reducer.updateNotificationCommentReadStatuses(state, {
            notification,
            eventIds,
            markedAsRead
        });
    }

    updateDotReadStatusFromActivities(state: State, {
        activity,
        eventIds
    }: ActivitiesActionPayloads.Select) {
        return this.reducer.updateNotificationCommentReadStatuses(state, {
            notification: activity,
            eventIds,
            markedAsRead: true
        });
    }

    removeComment(state: State, {
        pid,
        sid,
        did,
        cmid
    }: ScreenActionPayloads.RemoveComment): State {
        return this.reducer.removeDotComment(state, {
            pid, sid, daid: did, cmid
        });
    }

    removeDot(state: State, {
        pid,
        sid,
        did
    }: ScreenActionPayloads.RemoveDot): State {
        return this.reducer.removeDotAndAnnotation(
            state,
            { pid, sid, daid: did }
        );
    }

    updateDotPopupRemarkType(state: State, {
        pid,
        sid,
        did,
        remark,
        remarkType
    }: ScreenActionPayloads.UpdateDotPopupRemarkType): State {
        if (remarkType === RemarkType.Dot) {
            return this.createNote(state, { pid, sid, dot: (remark as DraftDot) });
        }

        return this.reducer.removeDotAndAnnotation(
            state,
            { pid, sid, daid: did! }
        );
    }

    updateUserData(state: State, {
        bid,
        uid,
        userData
    }: BarrelActionPayloads.UpdateUserData): State {
        const projectDots = state.dots[bid];

        // eslint-disable-next-line guard-for-in
        for (const screenId in projectDots) {
            const screenDots: Dot[] = projectDots[screenId];

            if (screenDots && screenDots.length) {
                const newScreenDots: Dot[] = screenDots.map(dot => {
                    const comments = dot.comments.map(comment => {
                        if (comment.author && comment.author._id === uid) {
                            return { ...comment, author: { ...comment.author, ...userData } };
                        }

                        return comment;
                    });

                    if (dot.creator && dot.creator._id === uid) {
                        return { ...dot, comments, creator: { ...dot.creator, ...userData } };
                    }

                    return { ...dot, comments };
                });

                return this.reducer.setDotsAndAnnotations(
                    state,
                    { pid: bid, sid: screenId, data: newScreenDots }
                );
            }
        }

        return state;
    }

    getApprovalsSuccess(state: State, {
        pid,
        aiReviews = []
    }: ApprovalsActionPayloads.GetProjectApprovalsSuccess): State {
        const aiReviewDots: BasicRecord<BasicRecord<BasicRecord<TransformedAIReviewDot[]>>> = {};

        for (const aiReview of aiReviews) {
            const {
                _id: arid,
                screens,
                createdAt,
                updatedAt,
                requestedBy
            } = aiReview;

            if (!aiReviewDots[pid]) {
                aiReviewDots[pid] = {};
            }

            if (!aiReviewDots[pid][arid]) {
                aiReviewDots[pid][arid] = {};
            }

            for (const screen of screens) {
                aiReviewDots[pid][arid][screen.id] = transformAIReviewDots(
                    screen.dots ?? [],
                    { createdAt, updatedAt, requestedBy }
                );
            }
        }

        return {
            ...state,
            aiReviewDots: {
                ...state.aiReviewDots,
                ...aiReviewDots
            }
        };
    }

    updateAIReview(state: State, {
        pid,
        airid,
        aiReview,
        update
    }: ApprovalsActionPayloads.UpdateAIReview): State {
        const updatedReviewScreenDotMap = Object.fromEntries(
            update.screens.map(updatedReviewScreen => [
                updatedReviewScreen.id,
                transformAIReviewDots(updatedReviewScreen.dots ?? [], {
                    createdAt: aiReview.createdAt,
                    updatedAt: aiReview.updatedAt,
                    requestedBy: aiReview.requestedBy
                })
            ])
        );

        return {
            ...state,
            aiReviewDots: {
                ...state.aiReviewDots,
                [pid]: {
                    ...state.aiReviewDots[pid],
                    [airid]: {
                        ...state.aiReviewDots[pid]?.[airid],
                        ...updatedReviewScreenDotMap
                    }
                }
            }
        };
    }

    createAIReviewScreenDot(state: State, {
        pid,
        airid,
        sid,
        aiReviewDot,
        aiReview
    }: ApprovalsActionPayloads.CreateAIReviewScreenDot): State {
        if (!aiReview) {
            return state;
        }

        const [dot] = transformAIReviewDots([aiReviewDot], {
            createdAt: aiReview.createdAt,
            updatedAt: aiReview.updatedAt,
            requestedBy: aiReview.requestedBy
        });

        const updatedDots = {
            ...state.aiReviewDots,
            [pid]: {
                ...state.aiReviewDots[pid],
                [airid]: {
                    ...state.aiReviewDots[pid]?.[airid],
                    [sid]: [
                        ...(state.aiReviewDots[pid]?.[airid]?.[sid] ?? []),
                        dot
                    ]
                }
            }
        };

        return {
            ...state,
            aiReviewDots: updatedDots
        };
    }

    deleteAIReviewScreenDot(state: State, {
        pid,
        airid,
        sid,
        did
    }: ApprovalsActionPayloads.DeleteAIReviewScreenDot): State {
        if (!pid || !airid || !sid || !did) {
            return state;
        }

        const updatedDots = state.aiReviewDots[pid]?.[airid]?.[sid]
            ?.filter(dot => dot._id !== did);

        return {
            ...state,
            aiReviewDots: {
                ...state.aiReviewDots,
                [pid]: {
                    ...state.aiReviewDots[pid],
                    [airid]: {
                        ...state.aiReviewDots[pid][airid],
                        [sid]: updatedDots
                    }
                }
            }
        };
    }

    // eslint-disable-next-line complexity
    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            // Screen action reducers
            case ScreenActionTypes.SET_DOTS:
                return this.setDots(state, action);

            case ScreenActionTypes.SELECT_DOT:
                return this.updateDotReadStatusFromSelectingDot(state, action);

            case ScreenActionTypes.CREATE_NOTE:
                return this.createNote(state, action);

            case ScreenActionTypes.UPDATE_DOT:
                return this.updateDot(state, action);

            case ScreenActionTypes.UPDATE_DOT_STATUS:
                return this.updateDotStatus(state, action);

            case ScreenActionTypes.UPDATE_DOT_POSITION:
                return this.updateDotPosition(state, action);

            case ScreenActionTypes.UPDATE_DOT_START_POSITION:
                return this.updateDotStartPosition(state, action);

            case ScreenActionTypes.UPDATE_DOT_COLOR:
                return this.updateDotColor(state, action);

            case ScreenActionTypes.ADD_DOT:
            case ScreenActionTypes.ADD_APPROVAL_PENDING_DOT:
                return this.addDot(state, action);

            case ScreenActionTypes.CREATE_COMMENT:
                return this.createComment(state, action);

            case ScreenActionTypes.ADD_COMMENT:
                return this.addComment(state, action);

            case ScreenActionTypes.UPDATE_COMMENT:
                return this.updateComment(state, action);

            case ScreenActionTypes.CREATE_REACTION:
                return this.createReaction(state, action);

            case ScreenActionTypes.ADD_REACTION:
                return this.addReaction(state, action);

            case ScreenActionTypes.UPDATE_REACTION:
                return this.updateReaction(state, action);

            case ScreenActionTypes.REMOVE_REACTION:
                return this.removeReaction(state, action);

            case ScreenActionTypes.REMOVE_COMMENT:
                return this.removeComment(state, action);

            case ScreenActionTypes.REMOVE_DOT:
                return this.removeDot(state, action);

            case ScreenActionTypes.DOTS_FLASHED:
                return this.dotsFlashed(state);

            case ScreenActionTypes.UPDATE_DOTPOPUP_REMARK_TYPE:
                return this.updateDotPopupRemarkType(state, action);

            case BarrelActionTypes.UPDATE_USER_DATA:
                return this.updateUserData(state, action);

            case BarrelActionTypes.LOAD_INTERMEDIATE:
                return this.setDotsForIntermediateLoading(state, action);

            // Project action reducers
            case ProjectActionTypes.SET_PROJECT_DOTS:
                return this.setProjectDots(state, action);

            // Dot status
            case DotCommentsActionTypes.UpdateDotCommentReadStatus:
                return this.updateCommentReadStatuses(state, action);

            case NotificationsActionTypes.SetEventReadStatus:
                return this.updateDotReadStatusFromNotifications(state, action);

            case ActivitiesActionTypes.Select:
                return this.updateDotReadStatusFromActivities(state, action);

            case ApprovalsActionTypes.GET_PROJECT_APPROVALS_SUCCESS:
                return this.getApprovalsSuccess(state, action);

            case ApprovalsActionTypes.UPDATE_AI_REVIEW:
                return this.updateAIReview(state, action);

            case ApprovalsActionTypes.CREATE_AI_REVIEW_SCREEN_DOT:
                return this.createAIReviewScreenDot(state, action);

            case ScreenActionTypes.UPDATE_AI_DOT_POSITION:
                return this.updateAIDotPosition(state, action);

            case ApprovalsActionTypes.UPDATE_AI_REVIEW_SCREEN_DOT:
                return this.updateAIDot(state, action);

            case ApprovalsActionTypes.UPDATE_AI_REVIEW_SCREEN_DOT_STATUS:
                return this.updateAIDotStatus(state, action);

            case ApprovalsActionTypes.CREATE_AI_REVIEW_SCREEN_DOT_RATING:
                return this.createAiReviewScreenDotRating(state, action);

            case ApprovalsActionTypes.DELETE_AI_REVIEW_SCREEN_DOT:
                return this.deleteAIReviewScreenDot(state, action);

            default:
                return state;
        }
    }
}

export default DotsDataStore;
export { State as DotsDataStoreState };
